import { apiClient } from './client';
import axios from 'axios';
import { useAuthStore } from '@/lib/auth/store';
import { config } from '@/config/env.config';

// Create a separate client for sync operations with longer timeout
const syncApiClient = axios.create({
  baseURL: config.api.baseUrl,
  timeout: 0, // No timeout for sync operations
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth interceptor for sync client
syncApiClient.interceptors.request.use((config) => {
  const { tokens } = useAuthStore.getState();
  if (tokens?.accessToken) {
    config.headers.Authorization = `Bearer ${tokens.accessToken}`;
  }
  config.headers['x-lang'] = 'pt-BR';
  return config;
});

export interface MovideskUser {
  id: string;
  name: string;
  email: string;
  userType: number;
}

export interface SyncResult<T> {
  created: number;
  updated: number;
  errors: string[];
  dataCreated: T[];
}

export interface DatabaseContract {
  number: string;
  id: string;
  startDate: Date | null;
  endDate: Date | null;
  contractedHours: number;
  consumedHours: number;
  minDebitTime: number;
  isActive: boolean;
  companyId: string;
  remoteId: string | null;
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date | null;
  company: {
    id: string;
    name: string;
    remoteId: string | null;
    createdAt: Date;
    updatedAt: Date;
    deletedAt: Date | null;
  };
}

export interface TicketSyncResult {
  created: number;
  updated: number;
  skipped: {
    userNotFound: number;
    contractNotFound: number;
    organizationNotFound: number;
    missingData: number;
  };
  errors: string[];
}

export const movideskApi = {
  getUsersForSelect: async (): Promise<MovideskUser[]> => {
    const response = await apiClient.get('/movidesk/users-for-select');
    return response.data;
  },

  syncContracts: async (): Promise<SyncResult<DatabaseContract>> => {
    const response = await apiClient.post('/movidesk/sync/contracts');
    return response.data;
  },

  syncTickets: async (params?: {
    startDate?: string;
    endDate?: string;
  }): Promise<TicketSyncResult> => {
    const response = await syncApiClient.post('/movidesk/sync/tickets', params);
    return response.data;
  },

  getLastSyncDates: async (): Promise<{
    lastTicketsSync: string | null;
    lastContractsSync: string | null;
  }> => {
    const response = await apiClient.get('/movidesk/sync/last-sync-dates');
    return response.data;
  },
};
